import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';

const AccountTab = () => {
  const { user, userProfile, updateEmail, updatePassword } = useAuth();
  
  const [emailData, setEmailData] = useState({
    currentEmail: '',
    newEmail: ''
  });
  
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [emailSaving, setEmailSaving] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      setEmailData(prev => ({
        ...prev,
        currentEmail: user?.email || ''
      }));
    }
  }, [user]);

  const handleEmailChange = (e) => {
    const { name, value } = e?.target;
    setEmailData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (emailError) setEmailError('');
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e?.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (passwordError) setPasswordError('');
  };

  const validateEmail = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailData?.newEmail) {
      newErrors.newEmail = 'Veuillez saisir un nouvel email';
    } else if (!emailRegex?.test(emailData?.newEmail)) {
      newErrors.newEmail = 'Email invalide';
    } else if (emailData?.newEmail === emailData?.currentEmail) {
      newErrors.newEmail = 'Le nouvel email doit être différent de l\'actuel';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};
    
    if (!passwordData?.newPassword) {
      newErrors.newPassword = 'Veuillez saisir un nouveau mot de passe';
    } else if (passwordData?.newPassword?.length < 8) {
      newErrors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
    }
    
    if (!passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Veuillez confirmer le mot de passe';
    } else if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateEmail()) {
      return;
    }

    setEmailSaving(true);
    setEmailError('');
    setEmailSuccess(false);
    
    try {
      const { error } = await updateEmail(emailData?.newEmail);
      
      if (error) {
        if (error?.message?.includes('Email rate limit exceeded')) {
          setEmailError('Trop de tentatives. Veuillez réessayer plus tard.');
        } else if (error?.message?.includes('already registered')) {
          setEmailError('Cet email est déjà utilisé');
        } else {
          setEmailError(error?.message || 'Erreur lors de la mise à jour de l\'email');
        }
        return;
      }
      
      setEmailSuccess(true);
      setEmailData(prev => ({
        currentEmail: emailData?.newEmail,
        newEmail: ''
      }));
      
      setTimeout(() => setEmailSuccess(false), 5000);
    } catch (err) {
      console.error('Email update error:', err);
      setEmailError('Une erreur est survenue');
    } finally {
      setEmailSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setPasswordSaving(true);
    setPasswordError('');
    setPasswordSuccess(false);
    
    try {
      const { error } = await updatePassword(passwordData?.newPassword);
      
      if (error) {
        if (error?.message?.includes('Password should be at least')) {
          setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
        } else {
          setPasswordError(error?.message || 'Erreur lors de la mise à jour du mot de passe');
        }
        return;
      }
      
      setPasswordSuccess(true);
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
      
      setTimeout(() => setPasswordSuccess(false), 5000);
    } catch (err) {
      console.error('Password update error:', err);
      setPasswordError('Une erreur est survenue');
    } finally {
      setPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Account Information */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="User" size={20} className="text-accent" />
          Informations du compte
        </h3>
        <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <Icon name="Mail" size={18} className="text-accent" />
              <div>
                <p className="text-sm font-medium text-primary">Email actuel</p>
                <p className="text-sm text-secondary">{emailData?.currentEmail}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={18} className="text-accent" />
              <div>
                <p className="text-sm font-medium text-primary">Rôle</p>
                <p className="text-sm text-secondary">
                  {userProfile?.role === 'practitioner_admin' ? 'Administrateur Praticien' : userProfile?.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Email */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Mail" size={20} className="text-accent" />
          Modifier l'adresse email
        </h3>
        
        {emailSuccess && (
          <div className="mb-4 bg-success/10 border border-success/30 rounded-lg p-4 flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <p className="text-sm text-secondary">Email mis à jour avec succès</p>
          </div>
        )}
        
        {emailError && (
          <div className="mb-4 bg-error/10 border border-error/30 rounded-lg p-4 flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <p className="text-sm text-secondary">{emailError}</p>
          </div>
        )}
        
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <Input
            label="Nouvel email"
            name="newEmail"
            type="email"
            value={emailData?.newEmail}
            onChange={handleEmailChange}
            placeholder="nouveau@email.fr"
            error={errors?.newEmail}
            disabled={emailSaving}
            required
          />
          
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEmailData(prev => ({ ...prev, newEmail: '' }));
                setErrors({});
                setEmailError('');
              }}
              disabled={!emailData?.newEmail || emailSaving}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              loading={emailSaving}
              disabled={!emailData?.newEmail || emailSaving}
              iconName="Save"
            >
              {emailSaving ? 'Mise à jour...' : 'Mettre à jour l\'email'}
            </Button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <Icon name="Lock" size={20} className="text-accent" />
          Modifier le mot de passe
        </h3>
        
        {passwordSuccess && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
            <Icon name="CheckCircle" size={20} className="text-green-600" />
            <p className="text-sm text-green-800">Mot de passe mis à jour avec succès</p>
          </div>
        )}
        
        {passwordError && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-red-600" />
            <p className="text-sm text-red-800">{passwordError}</p>
          </div>
        )}
        
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Exigences du mot de passe</p>
                <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                  <li>Au moins 8 caractères</li>
                  <li>Utilisez un mot de passe unique et sécurisé</li>
                </ul>
              </div>
            </div>
          </div>
          
          <Input
            label="Nouveau mot de passe"
            name="newPassword"
            type="password"
            value={passwordData?.newPassword}
            onChange={handlePasswordChange}
            placeholder="••••••••"
            error={errors?.newPassword}
            disabled={passwordSaving}
            required
          />
          
          <Input
            label="Confirmer le mot de passe"
            name="confirmPassword"
            type="password"
            value={passwordData?.confirmPassword}
            onChange={handlePasswordChange}
            placeholder="••••••••"
            error={errors?.confirmPassword}
            disabled={passwordSaving}
            required
          />
          
          <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setPasswordData({ newPassword: '', confirmPassword: '' });
                setErrors({});
                setPasswordError('');
              }}
              disabled={(!passwordData?.newPassword && !passwordData?.confirmPassword) || passwordSaving}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              loading={passwordSaving}
              disabled={!passwordData?.newPassword || !passwordData?.confirmPassword || passwordSaving}
              iconName="Save"
            >
              {passwordSaving ? 'Mise à jour...' : 'Mettre à jour le mot de passe'}
            </Button>
          </div>
        </form>
      </div>

      {/* Security Notice */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        <div className="flex items-start gap-3">
          <Icon name="Shield" size={20} className="text-gray-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-gray-900">Sécurité du compte</p>
            <p className="text-sm text-gray-600 mt-1">
              Vos identifiants de connexion sont cryptés et sécurisés. Nous vous recommandons de changer régulièrement votre mot de passe et d'utiliser un mot de passe unique pour ce compte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountTab;